import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Save, Search, Check, ChevronsUpDown } from "lucide-react";
import { HeaderPage } from "@/components/HeaderPage";
import { TextArea } from "@/components/TextArea";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import {
  createServiceOrderSchema,
  type CreateServiceOrderFormData,
} from "@/schemas/serviceOrderSchemas";
import { useCreateServiceOrder } from "@/hooks/useServiceOrders";
import { useGetSectors } from "@/hooks/useSectors";
import { useGetUsers } from "@/hooks/useUsers";
import { useGetTeams } from "@/hooks/useTeams";
import { useGetOcurrences } from "@/hooks/useOccurrences";
import { useDebounce } from "@/hooks/useDebounce";
import { MultiSelectTable } from "@/components/MultiSelectTable";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useState, useEffect, useRef, useMemo } from "react";
import type { OccurrenceDto } from "@/dtos/CategoriesResponseDto";

const priorityOptions = [
  { value: "LOW", label: "Baixa" },
  { value: "MEDIUM", label: "Média" },
  { value: "HIGH", label: "Alta" },
  { value: "URGENT", label: "Crítica" },
];

const roleLabels: Record<string, string> = {
  ADMIN: "Administrador",
  AGENT: "Agente de Campo",
  TRIAGE: "Triagem",
  AUDIT: "Auditoria",
  CITIZEN: "Cidadão",
};

const getEstimatedDaysByPriority = (priority: string): number => {
  switch (priority) {
    case "URGENT":
      return 1;
    case "HIGH":
      return 3;
    case "MEDIUM":
      return 7;
    case "LOW":
    default:
      return 15;
  }
};

const formatDateTimeLocal = (date: Date): string => {
  return date.toISOString().slice(0, 16);
};

export const CreateServiceOrder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const createServiceOrder = useCreateServiceOrder();
  const { data: sectorsData } = useGetSectors();
  const { data: teamsData } = useGetTeams();
  const { data: occurrencesData } = useGetOcurrences({ limit: 50 });

  const fromOccurrence = location.state?.fromOccurrence;
  const occurrence: OccurrenceDto | undefined = location.state?.occurrence;
  const duplicateData = location.state?.duplicateData;

  const [searchSector, setSearchSector] = useState("");
  const [searchResponsible, setSearchResponsible] = useState("");
  const [responsibleOpen, setResponsibleOpen] = useState(false);
  const [selectedResponsibleName, setSelectedResponsibleName] = useState("");
  const [teamOpen, setTeamOpen] = useState(false);
  const [selectedTeamName, setSelectedTeamName] = useState("");

  const debouncedSearchResponsible = useDebounce(searchResponsible, 500);

  // Buscar usuários para responsável (todas roles exceto CitizenProfile)
  const { data: usersData } = useGetUsers({
    role: "ADMIN,AGENT,TRIAGE,AUDIT",
    search: debouncedSearchResponsible,
    limit: 50,
  });

  const hasPrefilledRef = useRef(false);

  const defaultFormValues = useMemo(() => {
    // Se estiver duplicando uma ordem de serviço
    if (duplicateData) {
      return {
        title: duplicateData.title,
        description: duplicateData.description,
        priority: duplicateData.priority as
          | "LOW"
          | "MEDIUM"
          | "HIGH"
          | "URGENT",
        sectorId: duplicateData.sectorId,
        assignedToId: duplicateData.assignedToId,
        assignedTeamId: duplicateData.assignedTeamId,
        estimatedStartDate: duplicateData.estimatedStartDate
          ? new Date(duplicateData.estimatedStartDate)
              .toISOString()
              .slice(0, 16)
          : "",
        estimatedEndDate: duplicateData.estimatedEndDate
          ? new Date(duplicateData.estimatedEndDate).toISOString().slice(0, 16)
          : "",
        occurrenceIds: [],
      };
    }

    // Se estiver criando a partir de uma ocorrência
    if (fromOccurrence && occurrence) {
      const title = `OS - ${occurrence.category.name} - ${occurrence.protocol}`;
      const description = `Ordem de Serviço gerada a partir da ocorrência ${occurrence.protocol}.\n\nCategoria: ${occurrence.category.name}\nSubcategoria: ${occurrence.subcategory.name}\n\nDescrição da ocorrência:\n${occurrence.description}\n\nLocalização: ${occurrence.address}`;

      const now = new Date();
      const estimatedDays = getEstimatedDaysByPriority(occurrence.priority);
      const endDate = new Date(now.getTime() + estimatedDays * 24 * 60 * 60 * 1000);

      return {
        title,
        description,
        priority: occurrence.priority as "LOW" | "MEDIUM" | "HIGH" | "URGENT",
        occurrenceIds: [occurrence.id],
        sectorId: occurrence.sectorId,
        estimatedStartDate: formatDateTimeLocal(now),
        estimatedEndDate: formatDateTimeLocal(endDate),
      };
    }

    return {
      priority: "LOW" as const,
      occurrenceIds: [],
    };
  }, [fromOccurrence, occurrence, duplicateData]);

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateServiceOrderFormData>({
    resolver: zodResolver(createServiceOrderSchema),
    defaultValues: defaultFormValues,
  });

  const occurrenceIds = watch("occurrenceIds");

  useEffect(() => {
    setValue("title", defaultFormValues.title || "");
    setValue("description", defaultFormValues.description || "");
    setValue("priority", defaultFormValues.priority);
    setValue("occurrenceIds", defaultFormValues.occurrenceIds);
    if (defaultFormValues.sectorId) {
      setValue("sectorId", defaultFormValues.sectorId);
    }
    if (defaultFormValues.assignedToId) {
      setValue("assignedToId", defaultFormValues.assignedToId);
    }
    if (defaultFormValues.assignedTeamId) {
      setValue("assignedTeamId", defaultFormValues.assignedTeamId);
    }
    if (defaultFormValues.estimatedStartDate) {
      setValue("estimatedStartDate", defaultFormValues.estimatedStartDate);
    }
    if (defaultFormValues.estimatedEndDate) {
      setValue("estimatedEndDate", defaultFormValues.estimatedEndDate);
    }
  }, [defaultFormValues, setValue]);

  // Resolver nome do responsável pré-selecionado (duplicação de OS)
  useEffect(() => {
    if (defaultFormValues.assignedToId && usersData?.data && !selectedResponsibleName) {
      const user = usersData.data.find((u: any) => u.id === defaultFormValues.assignedToId);
      if (user) {
        setSelectedResponsibleName(user.profile?.fullName || user.email);
      }
    }
  }, [defaultFormValues.assignedToId, usersData, selectedResponsibleName]);

  // Resolver nome da equipe pré-selecionada (duplicação de OS)
  useEffect(() => {
    if (defaultFormValues.assignedTeamId && teamsData?.data && !selectedTeamName) {
      const team = teamsData.data.find((t: any) => t.id === defaultFormValues.assignedTeamId);
      if (team) {
        setSelectedTeamName(team.name);
      }
    }
  }, [defaultFormValues.assignedTeamId, teamsData, selectedTeamName]);

  useEffect(() => {
    if (fromOccurrence && occurrence && !hasPrefilledRef.current) {
      hasPrefilledRef.current = true;

      const message = occurrence.sectorId
        ? "Formulário pré-preenchido com dados da ocorrência! Selecione o responsável para continuar."
        : "Formulário pré-preenchido com dados da ocorrência! Selecione o setor e responsável para continuar.";

      toast.success(message);
    }
  }, [fromOccurrence, occurrence]);

  const filteredSectors = sectorsData?.data?.filter((sector: any) =>
    sector.name?.toLowerCase().includes(searchSector.toLowerCase()),
  );

  // Funções para gerenciar ocorrências
  const handleAddOccurrence = (occurrenceId: string) => {
    const newOccurrenceIds = [...occurrenceIds, occurrenceId];
    setValue("occurrenceIds", newOccurrenceIds);
  };

  const handleRemoveOccurrence = (occurrenceId: string) => {
    const newOccurrenceIds = occurrenceIds.filter((id) => id !== occurrenceId);
    setValue("occurrenceIds", newOccurrenceIds);
  };

  const onSubmit = async (data: CreateServiceOrderFormData) => {
    try {
      const result = await createServiceOrder.mutateAsync(data);
      toast.success("Ordem de serviço criada com sucesso!");
      if (fromOccurrence && result?.id) {
        navigate(`/service-orders/view/${result.id}`);
      } else {
        navigate("/service-orders");
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Erro ao criar ordem de serviço";
      toast.error(errorMessage);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <HeaderPage
        title="Criar Ordem de Serviço"
        description="Informe os dados da ordem de serviço"
      />

      <div className="flex-1 overflow-y-auto mt-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="bg-white">
            <CardContentUI className="p-4">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <h3 className="text-base font-semibold mb-3">
                    Informações Básicas
                  </h3>
                  <div className="grid grid-cols-1 gap-3">
                    <Input
                      title="Título"
                      isRequired
                      placeholder="Ex: Reparos em infraestrutura - Rua Principal"
                      error={errors.title?.message}
                      {...register("title")}
                    />

                    <TextArea
                      title="Descrição"
                      isRequired
                      placeholder="Descreva os detalhes da ordem de serviço"
                      {...register("description")}
                    />
                    {errors.description && (
                      <p className="text-sm text-red-500 mt-1">
                        {errors.description.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-semibold mb-3">
                    Classificação e Atribuição
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Controller
                      name="priority"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium">
                            Prioridade <span className="text-red-500">*</span>
                          </label>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a prioridade" />
                            </SelectTrigger>
                            <SelectContent>
                              {priorityOptions.map((option) => (
                                <SelectItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {errors.priority && (
                            <p className="text-sm text-red-500">
                              {errors.priority.message}
                            </p>
                          )}
                        </div>
                      )}
                    />

                    <Controller
                      name="sectorId"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium">
                            Setor <span className="text-red-500">*</span>
                          </label>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o setor" />
                            </SelectTrigger>
                            <SelectContent>
                              <div className="flex items-center px-3 pb-2">
                                <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                                <Input
                                  placeholder="Pesquisar setor..."
                                  value={searchSector}
                                  onChange={(e) =>
                                    setSearchSector(e.target.value)
                                  }
                                  className="h-8 w-full border-none p-0 focus-visible:ring-0"
                                />
                              </div>
                              {filteredSectors?.map((sector: any) => (
                                <SelectItem key={sector.id} value={sector.id}>
                                  {sector.name}
                                </SelectItem>
                              ))}
                              {filteredSectors?.length === 0 && (
                                <div className="py-6 text-center text-sm text-muted-foreground">
                                  Nenhum setor encontrado.
                                </div>
                              )}
                            </SelectContent>
                          </Select>
                          {errors.sectorId && (
                            <p className="text-sm text-red-500">
                              {errors.sectorId.message}
                            </p>
                          )}
                        </div>
                      )}
                    />

                    <Controller
                      name="assignedToId"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium">
                            Responsável <span className="text-red-500">*</span>
                          </label>
                          <Popover open={responsibleOpen} onOpenChange={setResponsibleOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={responsibleOpen}
                                className="w-full justify-between font-normal h-10"
                              >
                                {selectedResponsibleName || "Selecione o responsável"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                              <Command shouldFilter={false}>
                                <CommandInput
                                  placeholder="Pesquisar responsável..."
                                  value={searchResponsible}
                                  onValueChange={setSearchResponsible}
                                />
                                <CommandList>
                                  <CommandEmpty>
                                    {searchResponsible
                                      ? "Nenhum usuário encontrado"
                                      : "Digite para pesquisar..."}
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {usersData?.data?.map((user: any) => {
                                      const displayName = user.profile?.fullName || user.email;
                                      return (
                                        <CommandItem
                                          key={user.id}
                                          value={displayName}
                                          keywords={[displayName, user.email, user.role]}
                                          onSelect={() => {
                                            field.onChange(user.id);
                                            setSelectedResponsibleName(displayName);
                                            setResponsibleOpen(false);
                                            setSearchResponsible("");
                                          }}
                                        >
                                          <Check
                                            className={`mr-2 h-4 w-4 ${field.value === user.id ? "opacity-100" : "opacity-0"}`}
                                          />
                                          <div className="flex flex-col items-start">
                                            <span>{displayName}</span>
                                            <span className="text-xs text-gray-500">
                                              {roleLabels[user.role] || user.role}
                                            </span>
                                          </div>
                                        </CommandItem>
                                      );
                                    })}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          {errors.assignedToId && (
                            <p className="text-sm text-red-500">
                              {errors.assignedToId.message}
                            </p>
                          )}
                        </div>
                      )}
                    />

                    <Controller
                      name="assignedTeamId"
                      control={control}
                      render={({ field }) => (
                        <div className="space-y-1.5">
                          <label className="text-sm font-medium">
                            Equipe <span className="text-red-500">*</span>
                          </label>
                          <Popover open={teamOpen} onOpenChange={setTeamOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={teamOpen}
                                className="w-full justify-between font-normal h-10"
                              >
                                {selectedTeamName || "Selecione a equipe"}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                              <Command>
                                <CommandInput
                                  placeholder="Pesquisar equipe..."
                                />
                                <CommandList>
                                  <CommandEmpty>
                                    Nenhuma equipe encontrada.
                                  </CommandEmpty>
                                  <CommandGroup>
                                    {teamsData?.data?.map((team: any) => (
                                      <CommandItem
                                        key={team.id}
                                        value={team.name}
                                        onSelect={() => {
                                          field.onChange(team.id);
                                          setSelectedTeamName(team.name);
                                          setTeamOpen(false);
                                        }}
                                      >
                                        <Check
                                          className={`mr-2 h-4 w-4 ${field.value === team.id ? "opacity-100" : "opacity-0"}`}
                                        />
                                        {team.name}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                          {errors.assignedTeamId && (
                            <p className="text-sm text-red-500">
                              {errors.assignedTeamId.message}
                            </p>
                          )}
                        </div>
                      )}
                    />
                  </div>
                </div>

                <div>
                  <h3 className="text-base font-semibold mb-3">
                    Ocorrências Relacionadas
                  </h3>
                  <MultiSelectTable
                    title="Ocorrências"
                    items={occurrencesData?.data || []}
                    selectedIds={occurrenceIds || []}
                    onAdd={handleAddOccurrence}
                    onRemove={handleRemoveOccurrence}
                    searchPlaceholder="Pesquisar por protocolo..."
                    emptyMessage="Nenhuma ocorrência adicionada"
                    displayField={(occurrence) =>
                      `${occurrence.protocol || "Sem protocolo"} - ${occurrence.description?.substring(0, 50) || "Sem descrição"}...`
                    }
                  />
                </div>

                <div>
                  <h3 className="text-base font-semibold mb-3">
                    Prazos Estimados
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      title="Data de Início Estimada"
                      isRequired
                      type="datetime-local"
                      error={errors.estimatedStartDate?.message}
                      {...register("estimatedStartDate")}
                    />

                    <Input
                      title="Data de Término Estimada"
                      isRequired
                      type="datetime-local"
                      error={errors.estimatedEndDate?.message}
                      {...register("estimatedEndDate")}
                    />
                  </div>
                </div>

                <div className="flex gap-2 pt-3 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/service-orders")}
                    disabled={createServiceOrder.isPending}
                    className="cursor-pointer"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Voltar
                  </Button>
                  <Button
                    type="submit"
                    disabled={createServiceOrder.isPending}
                    isLoading={createServiceOrder.isPending}
                    className="text-white cursor-pointer"
                  >
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Criar Ordem de Serviço
                    </>
                  </Button>
                </div>
              </form>
            </CardContentUI>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};
